import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

interface ProfileData {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  badge_number?: string;
  station?: string;
  org_name?: string;
}

export default function Account() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    api.get<ProfileData>('/auth/me').then(data => {
      setProfile(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (newPassword.length < 10) {
      setPwError('Password must be at least 10 characters with uppercase, lowercase, number, and special character');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setPwError('New password must be different from current password');
      return;
    }

    setPwLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      setPwSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwError(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const roleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'quartermaster': return 'Quartermaster';
      default: return 'Standard User';
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: 24,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 2,
    fontWeight: 500,
  };

  const valueStyle: React.CSSProperties = {
    fontSize: 15,
    color: '#111827',
    marginBottom: 16,
    fontWeight: 500,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    boxSizing: 'border-box' as const,
  };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 24px', color: '#111827' }}>
        My Account
      </h1>

      {/* Profile Information */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 20px', color: '#374151' }}>
          Profile Information
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 32px' }}>
          <div>
            <div style={labelStyle}>Name</div>
            <div style={valueStyle}>{profile?.first_name} {profile?.last_name}</div>
          </div>
          <div>
            <div style={labelStyle}>Email</div>
            <div style={valueStyle}>{profile?.email}</div>
          </div>
          <div>
            <div style={labelStyle}>Role</div>
            <div style={valueStyle}>
              <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 600,
                background: profile?.role === 'admin' ? '#fef2f2' : profile?.role === 'quartermaster' ? '#eff6ff' : '#f0fdf4',
                color: profile?.role === 'admin' ? '#b91c1c' : profile?.role === 'quartermaster' ? '#1e40af' : '#166534',
              }}>
                {roleLabel(profile?.role || '')}
              </span>
            </div>
          </div>
          <div>
            <div style={labelStyle}>Department</div>
            <div style={valueStyle}>{profile?.org_name || '—'}</div>
          </div>
          {profile?.badge_number && (
            <div>
              <div style={labelStyle}>Badge Number</div>
              <div style={valueStyle}>{profile.badge_number}</div>
            </div>
          )}
          {profile?.station && (
            <div>
              <div style={labelStyle}>Station</div>
              <div style={valueStyle}>{profile.station}</div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 4, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#6b7280' }}>
          Contact your administrator to update profile information.
        </div>
      </div>

      {/* Change Password */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 20px', color: '#374151' }}>
          Change Password
        </h2>

        <form onSubmit={handleChangePassword} style={{ maxWidth: 400 }}>
          {pwError && (
            <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
              {pwError}
            </div>
          )}
          {pwSuccess && (
            <div style={{ background: '#f0fdf4', color: '#166534', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
              {pwSuccess}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#374151' }}>
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              style={inputStyle}
              placeholder="Enter current password"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#374151' }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              style={inputStyle}
              placeholder="Min 10 characters"
            />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Must include: uppercase, lowercase, number, and special character
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#374151' }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              style={inputStyle}
              placeholder="Re-enter new password"
            />
          </div>

          <button
            type="submit"
            disabled={pwLoading}
            style={{
              padding: '10px 24px',
              background: '#b91c1c',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              opacity: pwLoading ? 0.7 : 1,
            }}
          >
            {pwLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Session Info */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 12px', color: '#374151' }}>
          Session
        </h2>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          You are currently signed in as <strong>{user?.email}</strong>. Your session will expire after 24 hours of inactivity.
        </div>
      </div>
    </div>
  );
}
